/**
 * HRM feature permissions — the single source of truth for what a role can do in this app.
 *
 * Every entry is a Blocks IAM `FrontendAction` permission (type 2), published to the project with
 * `npm run permissions:publish`. Roles are granted these in Blocks IAM (`npm run permissions:grant -- --role <slug>`); the UI only ever asks
 * "does the signed-in user hold this permission?" (`<ProtectedRoute permissions>`,
 * `<ProtectedFragment permissions>`, `useHasPermission`) and never checks a role name.
 *
 * Resource format: `hrm::<module>::<action>`, e.g. `hrm::department::view`.
 * Resource group: the module (`department`), so each module is published and assigned on its own.
 *
 * This file must stay free of imports so the publish script can load it directly with Node.
 */

/** Prefix of every resource string and the first tag, so the app's permissions are easy to find. */
export const HRM_PERMISSION_PREFIX = 'hrm';

/** Blocks IAM `ResourceType.FrontendAction`. */
const FRONTEND_ACTION_PERMISSION_TYPE = 2;

/** Blocks IAM `PermissionSeverity` — 1 is the most severe, 4 the least. */
type PermissionSeverity = 1 | 2 | 3 | 4;

type ModuleDefinition = {
  label: string;
  /** Action key → what it lets the holder do. */
  actions: Record<string, string>;
};

const MODULES = {
  PROFILE: {
    label: 'Profile',
    actions: {
      REQUIRE_EXTENDED_INFO:
        'Must complete the extended own profile (personal, academic, contact, address and emergency contact details) after login before using the rest of the app',
      LUNCH_ENABLED: 'Lunch service is on for the holder: they can request lunch',
      OVERTIME_ENABLED: 'Overtime is on for the holder',
      CONTACT_PERMANENT_ADDRESS: 'Contact info shows the permanent address section',
      CONTACT_REFERENCES: 'Contact info shows the reference details section',
      CONTACT_SOCIAL_PROFILES: 'Contact info shows the social profiles section (LinkedIn)',
      CONTACT_GITHUB_PROFILE: 'Social profiles include a GitHub profile',
      CONTACT_MESSENGER_PROFILES:
        'Social profiles include Facebook, WhatsApp and Instagram profiles',
    },
  },
  DIRECTORY: {
    label: 'Employee Directory',
    actions: {
      VIEW: 'View the employee directory and employee profiles',
      CREATE: 'Create new employees',
      UPDATE: 'Edit existing employees',
      VIEW_PERSONAL_INFO:
        "View and edit an employee's personal and academic details (family, ID documents, education, previous employment)",
      CHANGE_WFH:
        'See the work from home column in the employee directory, switch work from home on or off for an employee and choose their office days',
      CREATE_FINE: 'Add a lunch or non-compliance fine to an employee',
      ASSIGN_ROLE: 'Assign or change the roles an employee holds in each organization',
    },
  },
  DEPARTMENT: {
    label: 'Department',
    actions: {
      VIEW: 'View departments',
      VIEW_ALL_DEPARTMENTS:
        'See every department in filters and lists across the app (employee directory, attendance, timesheets, service desk, performance reviews)',
      VIEW_OWN_DEPARTMENT:
        'See only the department on the own profile of the user plus every department whose Head of Department role (hrm::department-scope::<key>) the user holds, in filters and lists across the app',
      CREATE: 'Create departments',
      UPDATE: 'Edit departments',
      DELETE: 'Delete departments',
    },
  },
  ROLE: {
    label: 'Role',
    actions: {
      VIEW: 'View the roles of the organization and what they grant (Role Manager)',
      CREATE: 'Create roles of the organization',
      UPDATE: 'Rename roles of the organization and choose what they grant in it',
      DELETE: 'Delete roles of the organization nobody holds (permanent)',
    },
  },
  DESIGNATION: {
    label: 'Designation',
    actions: {
      CREATE: 'Create designations',
      UPDATE: 'Edit designations',
    },
  },
  LOCATION: {
    label: 'Location',
    actions: {
      CREATE: 'Create locations',
      UPDATE: 'Edit locations',
      DELETE: 'Delete locations',
    },
  },
  PROJECT: {
    label: 'Project',
    actions: {
      CREATE: 'Create projects',
      UPDATE: 'Edit projects',
      DELETE: 'Delete projects',
    },
  },
  SKILL_CATEGORY: {
    label: 'Skill Category',
    actions: {
      CREATE: 'Create skill categories',
      UPDATE: 'Edit skill categories',
      DELETE: 'Delete skill categories',
    },
  },
  SKILL_SUB_CATEGORY: {
    label: 'Skill Sub-category',
    actions: {
      CREATE: 'Create skill sub-categories',
      UPDATE: 'Edit skill sub-categories',
      DELETE: 'Delete skill sub-categories',
    },
  },
  SKILL: {
    label: 'Skill',
    actions: {
      CREATE: 'Create skills under a skill sub-category',
      UPDATE: 'Edit skills',
    },
  },
  NOC_TYPE: {
    label: 'NOC Type',
    actions: {
      CREATE: 'Create NOC types',
      UPDATE: 'Edit NOC types',
      DELETE: 'Delete NOC types',
    },
  },
  CTC_LEVEL: {
    label: 'CTC Level',
    actions: {
      CREATE: 'Create CTC levels',
      UPDATE: 'Edit CTC levels',
      DELETE: 'Delete CTC levels',
    },
  },
  KPI: {
    label: 'KPI',
    actions: {
      CONFIGURE: 'Change the KPI scoring configuration',
    },
  },
  LOGS: {
    label: 'Activity Logs',
    actions: {
      VIEW: 'View the activity logs',
    },
  },
  ATTENDANCE: {
    label: 'Attendance',
    // Features (what the holder may do) and rules (how the attendance-rule-catalog.json rule with
    // the same permission binds them). In a rule group the held rule with the highest priority wins.
    actions: {
      VIEW_OWN: 'View own attendance',
      CLOCK: 'Clock in and clock out',
      TAKE_BREAK: 'Take and end regular breaks while clocked in (any number per day)',
      MULTIPLE_SESSIONS:
        'Clock in again on the same day after clocking out; the time in between is neither work nor break',
      TAKE_LUNCH_BREAK:
        'Take one lunch break per day, only between 10:00 and 14:00 local time; it always counts at least 30 minutes, the day is charged at least 30 minutes of break, and a 13:30-14:00 lunch is recorded at clock-out when no break was taken',
      LUNCH_WINDOW_AFTER_CLOCK_IN:
        'Take one lunch break per day, from 2 h to 6 h after the first clock-in; it counts at least 30 minutes; no lunch is added when none was taken (wins over Take Lunch Break)',
      REQUIRE_CLOCK_IN_BEFORE_CUTOFF:
        'Cannot clock in at or after 16:00 local time (flexible-hours employees are exempt)',
      REQUIRE_CLOCK_OUT_WAIT: 'Clock-out is accepted only one hour after clock-in',
      REQUIRE_FULL_DAY_HOURS:
        'A correction must request at least the working hours of the day in the HR term (full day) or half of them (half day) of clock time',
      REQUIRE_MIN_BREAK_LENGTH: 'Every regular break must last at least 15 minutes',
      REQUIRE_BREAKS_30_45:
        'Minimum total break per day: 30 minutes for more than 6 hours of work, 45 minutes for more than 9 hours',
      REQUIRE_BREAKS_15_30_60:
        'Minimum total break per day: 15 minutes for more than 5:30 hours of work, 30 minutes for more than 7 hours, 60 minutes for more than 9 hours (wins over the 30/45 rule)',
      REQUIRE_MAX_WORK_HOURS: 'Worked time on a correction cannot exceed 10 hours',
      REQUIRE_OFFICE_IP:
        'Clock in/out and breaks only from an approved office network, unless the employee works from home. Off while no office address is configured; with addresses configured the holder is refused until the platform can report the caller address',
      REQUIRE_CAPTCHA_ON_CLOCK_OUT:
        'Clock-out needs a solved captcha (reCAPTCHA), verified on the server before the day is saved',
      NON_WORKDAY_OVERTIME_BONUS:
        'Work on a holiday or a day off is credited as overtime with a bonus: 150% of the worked time',
      FORCE_CLOCK_OUT_AT_MAX_WORK:
        'Clocked out automatically once worked time reaches 10 hours (without overtime: the expected hours of a shorter day)',
      FORCE_CLOCK_OUT_AT_DAY_END:
        'Clocked out automatically at 23:59 local time when still clocked in',
      REMIND_NOT_CLOCKED_OUT: 'Mail and notification at 23:30 local time when still clocked in',
      REMIND_WORK_HOURS: 'Mail and notification after 7:45 and 9:45 hours of work',
      BREAK_REMINDERS:
        'Reminder after 30 minutes of lunch; after 45 minutes of lunch or 20 minutes of a regular break the department heads are told too',
      NOTIFY_BREAKS_TO_MANAGERS:
        "The holder's department heads are notified when a break starts or ends",
      REQUEST_CORRECTION: 'Request a correction to own attendance',
      CORRECTION_FINE:
        'Own attendance correction requests carry a fine, charged once per day when approved',
      VIEW_ALL:
        'Show Manage Attendance in the sidebar and open it, including each employee attendance history. Which employees are listed comes from the Department permissions',
      REVIEW_CORRECTION:
        'Approve or reject attendance correction requests (admins, or heads of the request department), one by one or in bulk',
      WAIVE_CORRECTION_FINE:
        'Waive the fine of an approved attendance correction (admins, or heads of the request department)',
      EXPORT: 'Export the Manage Attendance list as CSV or PDF',
      IMPORT:
        'Import attendance from a door/device punch log or a device export CSV (employees of the departments the holder may see; days with a record are left as they are)',
    },
  },
  LEAVE: {
    label: 'Leave',
    // Features (what the holder may do) and rule-switch permissions (how the matching
    // leave-rule-catalog.json rule with the same permission binds them). In a rule group the held
    // rule with the highest priority wins. Nothing here depends on a country or region.
    actions: {
      VIEW_OWN: 'View own leave balance and history',
      REQUEST: 'Submit leave requests',
      WITHDRAW:
        'Withdraw own leave requests: pending ones, and approved ones until the end of their start day',
      CANCEL:
        'Cancel approved leave requests of others (never own requests; heads only for their departments)',
      VIEW_CALENDAR:
        'Show the leave calendar in the sidebar and open it. Which leave it lists comes from the data rules (heads: their departments, admins: all)',
      VIEW_ALL:
        'Show Leave Manager in the sidebar and open it. Which requests are listed comes from the Department permissions (admins see all)',
      REVIEW:
        'Approve or reject leave requests (never own requests, regardless of this permission)',
      ARCHIVE:
        'Archive reviewed leave requests (never own requests; heads only for their departments)',
      VIEW_ACCUMULATED_LEAVE: 'Own leave allocation shows the accumulated leave column',
      VIEW_SHEET:
        'My Leave shows the Leave Sheet tab: own leave ledger (default leave, TOIL and missed attendance) of the HR term',
      VIEW_OVERVIEW:
        'My Leave shows the Leave Overview tab: own allocated, taken, requested and remaining leave per category',
      REQUIRE_NOTICE_PERIOD:
        'A leave request must be submitted at least this many days before its start date',
      REQUIRE_MAX_CONSECUTIVE_DAYS: 'A single leave request cannot span more than this many days',
      ALLOW_CROSS_HR_PERIOD:
        'A leave request may span two HR periodical profiles instead of being confined to one',
      COUNT_TOIL_IN_DEFAULT_LEAVE:
        'Approved TOIL and TOIL carry-forward add to the default leave of the HR term, which uses TOIL first',
      COUNT_TOIL_IN_DEFAULT_LEAVE_SPLIT:
        'Like count TOIL in default leave, with weekend and special TOIL kept as separate balances',
      DEDUCT_MISSED_ATTENDANCE:
        'A past working day without attendance or leave is charged to the default leave (half for a half day)',
    },
  },
  SICK_LEAVE: {
    label: 'Sick Leave',
    actions: {
      VIEW: 'View sick leave requests',
      REQUEST: 'Request sick leave on behalf of an employee',
      REQUIRE_CERTIFICATE_ABOVE_DAYS:
        "A sick leave needs a doctor's document once it takes this many working days or more, counted with the sick leave right before and after it",
      ALLOW_DOCTOR_SENT_CERTIFICATE:
        'A sick leave may say the doctor sends the document instead of uploading it',
    },
  },
  LEAVE_CATEGORY: {
    label: 'Leave Category',
    actions: {
      CREATE: 'Create leave categories',
      UPDATE: 'Edit leave categories',
      DELETE: 'Delete leave categories',
    },
  },
  HOLIDAY: {
    label: 'Holiday',
    actions: {
      // `hrm::holiday::view` is archived in dev and can't be restored, hence VIEW_LIST.
      VIEW_LIST: 'View the holiday list (read only)',
      CREATE: 'Create holidays',
      UPDATE: 'Edit holidays',
      DELETE: 'Delete holidays',
      MARK_CANTON_BASED: 'Mark a holiday as canton-based and assign its cantons',
    },
  },
  TOIL: {
    label: 'TOIL',
    actions: {
      VIEW_OWN: 'View own time off in lieu',
      REQUEST: 'Submit time off in lieu requests',
      REQUEST_FOR_OTHERS:
        'Submit time off in lieu for an employee, past days included (heads only for their departments)',
      VIEW_ALL: 'View the time off in lieu requests of all employees',
      REVIEW: 'Approve or reject time off in lieu requests',
      ARCHIVE: 'Archive reviewed time off in lieu requests',
      DELETE: 'Delete time off in lieu requests',
    },
  },
  OVERTIME: {
    label: 'Overtime',
    actions: {
      VIEW: 'View overtime of all employees',
      PAYOFF: 'Pay off overtime',
      REQUIRE_PAYOFF_NOTE: 'A note is mandatory when paying off overtime',
    },
  },
  TIMESHEET: {
    label: 'Timesheet',
    actions: {
      VIEW_OWN: 'View own timesheet',
      VIEW_ALL: 'View the timesheets of all employees',
    },
  },
  MBM_PROJECT: {
    label: 'MBM Project',
    actions: {
      CREATE: 'Add MBM projects',
      UPDATE: 'Edit MBM projects and activate or deactivate them',
    },
  },
  LUNCH: {
    label: 'Lunch',
    actions: {
      VIEW_OWN: 'View own lunch requests',
      REQUEST: 'Request lunch',
      CANCEL: 'Cancel own lunch requests',
      VIEW_ALL: 'View and manage the lunch requests of all employees',
    },
  },
  PERFORMANCE_FORM: {
    label: 'Performance Form',
    actions: {
      CREATE: 'Create performance review forms',
      UPDATE: 'Edit performance review forms',
      DELETE: 'Delete performance review forms',
    },
  },
  PERFORMANCE_REVIEW: {
    label: 'Performance Review',
    actions: {
      VIEW: 'View performance reviews and their details',
    },
  },
  PERFORMANCE_SUBMISSION: {
    label: 'Performance Submission',
    actions: {
      VIEW_OWN: 'View own performance review submissions',
    },
  },
  PEER_REQUEST: {
    label: 'Peer Request',
    actions: {
      VIEW: 'View peer review requests',
    },
  },
  PERFORMANCE_DASHBOARD: {
    label: 'Performance Dashboard',
    actions: {
      VIEW: 'View the own performance dashboard',
      VIEW_ALL: 'View the performance dashboard of any employee',
    },
  },
  POTENTIAL_PROFILE: {
    label: 'Potential Profile',
    actions: {
      VIEW: 'View potential profiles',
      CREATE: 'Create potential profiles',
    },
  },
  CONDITIONAL_OFFER: {
    label: 'Conditional Offer',
    actions: {
      VIEW: 'View conditional offers',
      CREATE: 'Create conditional offers',
    },
  },
  SURVEY_FORM: {
    label: 'Survey Form',
    actions: {
      CREATE: 'Create survey forms',
      UPDATE: 'Edit survey forms',
      DELETE: 'Delete survey forms',
    },
  },
  SURVEY: {
    label: 'Survey',
    actions: {
      VIEW: 'View survey batches',
      VIEW_RESPONSE: 'View the individual responses of a survey',
    },
  },
  SURVEY_SUBMISSION: {
    label: 'Survey Submission',
    actions: {
      VIEW_OWN: 'View own survey submissions',
    },
  },
  NOC_APPLICATION: {
    label: 'NOC Application',
    actions: {
      VIEW: 'View own NOC applications',
      APPLY: 'Submit and edit own NOC applications',
      WITHDRAW: 'Withdraw own pending NOC applications',
      VIEW_REQUESTS: 'View the NOC applications of employees',
      REVIEW: 'Approve or reject NOC applications',
      GENERATE: 'Generate the NOC letter of an approved application',
      DELETE: 'Delete any pending or rejected NOC application',
    },
  },
  BYOD_APPLICATION: {
    label: 'BYOD Application',
    actions: {
      VIEW: 'View own BYOD applications',
      APPLY: 'Submit and edit own BYOD applications',
      WITHDRAW: 'Withdraw own pending BYOD applications',
      VIEW_REQUESTS: 'View the BYOD applications of employees',
      REVIEW: 'Approve, reject or revert BYOD applications',
    },
  },
  BUSINESS_CARD_APPLICATION: {
    label: 'Business Card Application',
    actions: {
      VIEW: 'View own business card applications',
      APPLY: 'Submit and edit own business card applications',
      WITHDRAW: 'Withdraw own business card applications while they are still pending',
      VIEW_REQUESTS: 'View the business card applications of employees',
      UPDATE_STATUS: 'Move business card applications through the fulfilment stages',
    },
  },
  ID_CARD_APPLICATION: {
    label: 'ID Card Application',
    actions: {
      VIEW: 'View own ID card applications',
      APPLY: 'Submit and edit own ID card applications',
      WITHDRAW: 'Withdraw own ID card applications while they are still pending',
      VIEW_REQUESTS: 'View the ID card applications of employees',
      UPDATE_STATUS: 'Move ID card applications through the fulfilment stages',
    },
  },
  METLIFE_APPLICATION: {
    label: 'MetLife Application',
    actions: {
      VIEW: 'View own insurance applications',
      APPLY: 'Submit and edit own insurance applications',
      WITHDRAW: 'Withdraw own pending insurance applications',
      CANCEL: 'Cancel own completed spouse insurance policy',
      VIEW_REQUESTS: 'View the insurance applications of employees',
      UPDATE_STATUS: 'Move insurance applications through the processing stages',
      EXCLUDE: 'Exclude a completed insurance application from the policy',
    },
  },
} as const satisfies Record<string, ModuleDefinition>;

type Modules = typeof MODULES;

export type HrmPermissions = {
  [M in keyof Modules]: { [A in keyof Modules[M]['actions']]: string };
};

export type HrmPermissionDefinition = {
  name: string;
  description: string;
  /** `hrm::<module>::<action>` — what `user.permissions` contains. */
  resource: string;
  resourceGroup: string;
  type: typeof FRONTEND_ACTION_PERMISSION_TYPE;
  severity: PermissionSeverity;
  tags: string[];
};

const toKebab = (key: string) => key.toLowerCase().replace(/_/g, '-');

const toTitle = (key: string) =>
  key
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Deletes, payments, reviews, role grants and cross-employee reads expose more than routine work does. */
const HIGH_SEVERITY_ACTION =
  /^(DELETE|PAYOFF|REVIEW|VIEW_ALL|VIEW_REQUESTS|VIEW_RESPONSE|CONFIGURE|GENERATE|EXCLUDE|ASSIGN_ROLE)/;

const severityFor = (action: string): PermissionSeverity => {
  if (HIGH_SEVERITY_ACTION.test(action)) return 2;
  if (action.startsWith('VIEW')) return 4;
  return 3;
};

const resourceFor = (module: string, action: string) =>
  `${HRM_PERMISSION_PREFIX}::${toKebab(module)}::${toKebab(action)}`;

const entries = Object.entries(MODULES) as [string, ModuleDefinition][];

/** `HRM_PERMISSIONS.DEPARTMENT.VIEW` → `'hrm::department::view'`. */
export const HRM_PERMISSIONS = Object.fromEntries(
  entries.map(([module, { actions }]) => [
    module,
    Object.fromEntries(Object.keys(actions).map((action) => [action, resourceFor(module, action)])),
  ])
) as HrmPermissions;

/** Every permission this app defines, in the shape `blocks iam permissions create` expects. */
export const HRM_PERMISSION_CATALOG: HrmPermissionDefinition[] = entries.flatMap(
  ([module, { label, actions }]) =>
    Object.entries(actions).map(([action, description]) => ({
      name: `${label}: ${toTitle(action)}`,
      description,
      resource: resourceFor(module, action),
      // One resource group per module, so Blocks IAM lists and assigns permissions module by module.
      resourceGroup: toKebab(module),
      type: FRONTEND_ACTION_PERMISSION_TYPE,
      severity: severityFor(action),
      tags: [HRM_PERMISSION_PREFIX, toKebab(module)],
    }))
);
